<template>
    <div>
        <div class="columns">
            <div class="column is-12">
                <MatSelectionBar
                    ref="matSelectionBar"
                    :event-id="store.eventId"
                    @initialized="matsInitialized"
                    @selectedMat="selectedMat"
                />
            </div>
        </div>

        <div class="columns">
            <div class="column is-8">
                <table class="table is-hoverable is-fullwidth" v-if="verificationCandidates.competitors.length > 0">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{{ translate("common.first_name_and_last_name") }}</th>
                            <th>{{ translate("common.academy") }}</th>
                            <th>{{ translate("common.category") }}</th>
                            <th>{{ translate("common.mat") }}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(candidate, index) in verificationCandidates.competitors" :key="candidate.id">
                            <td>{{ index + 1 }}</td>
                            <td class="clarity-mask">{{ candidate.firstName }} {{ candidate.lastName }}</td>
                            <td>{{ candidate.academy }}</td>
                            <td>{{ candidate.category }}</td>
                            <td>{{ candidate.mat.name }}</td>
                            <th>
                                <button class="button is-small" @click="openVerification(candidate)">
                                    {{ translate("common.edit") }}
                                </button>
                            </th>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="column is-4">
                <table class="table is-hoverable is-fullwidth" v-if="verificationCandidates.overallProgress.length > 0">
                    <thead>
                        <tr>
                            <th>{{ translate("common.mat") }}</th>
                            <th>{{ translate("admin_verification.remaining_verifications") }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="matProgress in verificationCandidates.overallProgress" :key="matProgress.mat.id">
                            <td>{{ matProgress.mat.name }}</td>
                            <td>{{ matProgress.totalVerifications }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <VerificationModal ref="verificationModal" @finished="fetchVerificationQueue" :event-types="eventTypes" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import VerificationModal from "./VerificationModal.vue";
    import MatSelectionBar from "../Common/MatSelectionBar.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import history from "../Common/history";
    import { CompetitorWithCategory } from "../Api/eventCompetitors";
    import { Status } from "../Common/Competitors/types";
    import verifications, { VerificationCandidate, VerificationQueue } from "../Api/verifications";
    import { useVerificationStore } from "./verificationStore";

    const historyInstance = history.getInstance(window);

    defineProps<{
        eventTypes: string[];
    }>();

    const store = useVerificationStore();

    const matSelectionBar = ref<ComponentExposed<typeof MatSelectionBar> | null>(null);
    const verificationModal = ref<ComponentExposed<typeof VerificationModal> | null>(null);

    const queueInitialized = ref(false);
    const verificationCandidates = ref<VerificationQueue>({
        competitors: [],
        overallProgress: [],
    });

    function selectedMat() {
        if (!queueInitialized.value) {
            return;
        }
        updateLocation();
        return fetchVerificationQueue().catch(handleError);
    }

    function openVerification(candidate: VerificationCandidate) {
        //Verification candidate does not have all required fields, some can be assumed (status = 0).
        const competitor: CompetitorWithCategory = {
            id: candidate.id,
            firstName: candidate.firstName,
            lastName: candidate.lastName,
            academy: candidate.academy,
            branch: "",
            category: candidate.category,
            status: { value: 0 } as Status,
            eventRegistrationSpotNumber: null,
        };
        verificationModal.value?.open(competitor);
    }

    function matsInitialized() {
        updateLocation();
        fetchVerificationQueue()
            .then(() => {
                queueInitialized.value = true;
            })
            .catch(handleError);
    }

    function fetchVerificationQueue() {
        return verifications.fetchVerificationQueue(store.eventId, getMatIdsToQuery()).then(candidates => {
            verificationCandidates.value = candidates;
        });
    }

    function updateLocation() {
        historyInstance.setQueryParam("mat", getMatIdsToQuery());
    }

    function getSelectedMatIds() {
        return matSelectionBar.value?.getSelectedMats() || [];
    }

    function getMatIdsToQuery() {
        return getSelectedMatIds().join(",");
    }
</script>

<style scoped>
    .table th {
        vertical-align: middle;
    }

    .table td {
        vertical-align: middle;
    }
</style>
